import { Link } from 'react-router-dom';

const CALENDLY_URL = 'https://calendly.com/admin-dailygrinddesign/30min';

const valuesData = [
  {
    title: 'Craft Over Cookie-Cutter',
    description: 'Every project starts from a blank canvas. No templates, no shortcuts — just honest, original work tailored to your brand.',
    iconPath: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  },
  {
    title: 'Speed You Can Feel',
    description: 'A 24-hour first response and turnarounds built around your launch date — because design momentum matters as much as design quality.',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    title: 'Partners, Not Vendors',
    description: 'We invest in the long-term wins of every business we work with. Most of our clients come back — many become friends.',
    iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  },
];

const reasonsList = [
  'Hand-crafted designs that capture attention and convert',
  'Transparent pricing — no surprise fees, ever',
  '24-hour response time on every inquiry',
  'Unlimited revisions until you love it',
  'Print-ready and developer-ready file delivery',
  'Built specifically for small businesses & startups',
];

const statsData = [
  { value: '500+', label: 'Projects shipped', solid: true },
  { value: '98%', label: 'Client satisfaction', solid: false },
  { value: '24hr', label: 'First response', solid: false },
  { value: '100%', label: 'Hand-crafted', solid: true },
];

const AboutHero = () => (
  <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-br from-red-50 to-white">
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-red-600 font-semibold uppercase tracking-wider mb-4" data-testid="about-eyebrow">About Daily Grind Design</p>
        <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
          We build brands the
          <span className="bg-gradient-to-r from-red-600 to-black bg-clip-text text-transparent"> hard way</span> — by hand.
        </h1>
        <p className="text-xl text-gray-600 leading-relaxed">
          Daily Grind Design is a small, sharp design studio for small businesses and startups who want a brand that actually moves the needle.
        </p>
      </div>
    </div>
  </section>
);

const StorySection = () => (
  <section className="py-20 bg-white">
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8" data-testid="about-story-heading">Our Story</h2>
        <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
          <p>Daily Grind Design started with a simple frustration: too many small businesses were stuck choosing between cheap, lifeless template work and overpriced agencies that didn't really care about their projects.</p>
          <p>We built Daily Grind to fill that gap. Every flyer, logo, and website we deliver is hand-designed for your specific audience, your specific market, and your specific goals — at a price that respects the fact that you're growing a business, not flushing cash.</p>
          <p>Today we partner with restaurants, fitness studios, tech startups, and event organizers across the country. Every project gets the same treatment: real conversation, real iteration, real results.</p>
        </div>
      </div>
    </div>
  </section>
);

const ValueCard = ({ title, description, iconPath }) => (
  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300">
    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-black rounded-xl flex items-center justify-center mb-6">
      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
      </svg>
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const ValuesSection = () => (
  <section className="py-20 bg-gray-50">
    <div className="container mx-auto px-6">
      <div className="max-w-4xl mx-auto text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-testid="about-values-heading">What We Stand For</h2>
        <p className="text-xl text-gray-600">Our mission is simple: make brand-quality design accessible to the businesses that need it most. Here's how we live that out every day.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {valuesData.map((v) => <ValueCard key={v.title} {...v} />)}
      </div>
    </div>
  </section>
);

const StatBlock = ({ value, label, solid }) => (
  <div className={`rounded-2xl p-8 shadow-xl ${solid ? 'bg-gradient-to-br from-red-600 to-black text-white' : 'bg-white border-2 border-red-100'}`}>
    <div className={`text-5xl font-bold mb-2 ${solid ? '' : 'text-red-600'}`}>{value}</div>
    <div className={solid ? 'text-red-100' : 'text-gray-600'}>{label}</div>
  </div>
);

const WhyChooseSection = () => (
  <section className="py-20 bg-white">
    <div className="container mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
        <div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-testid="about-why-heading">
            Why teams choose
            <span className="bg-gradient-to-r from-red-600 to-black bg-clip-text text-transparent"> Daily Grind</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">When you hire us, you're not buying a deliverable — you're hiring an extension of your team. Here's what's included with every project:</p>
          <ul className="space-y-4">
            {reasonsList.map((reason) => (
              <li key={reason} className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-lg">{reason}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {statsData.map((s) => <StatBlock key={s.value} {...s} />)}
        </div>
      </div>
    </div>
  </section>
);

const AboutCTA = () => (
  <section className="py-20 bg-gradient-to-r from-red-600 to-black">
    <div className="container mx-auto px-6 text-center">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Ready to grind with us?</h2>
        <p className="text-xl text-red-100 mb-8">Tell us about your project, and we'll come back within 24 hours with a plan to bring your brand to life.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/#contact" data-testid="about-cta-contact" className="bg-white text-red-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all shadow-lg">Start a Project</Link>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="about-cta-book-call" className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-red-600 transition-all">Book a Call</a>
        </div>
      </div>
    </div>
  </section>
);

const About = () => (
  <div className="App">
    <AboutHero />
    <StorySection />
    <ValuesSection />
    <WhyChooseSection />
    <AboutCTA />
  </div>
);

export default About;
